"use client";

import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import type { HTMLAttributes } from "react";
import {
  type FieldPath,
  type FieldValues,
  useController,
  useFormContext,
} from "react-hook-form";
import { useReverseGeocode } from "@/hooks/useReverseGeocode";
import { cn } from "@/lib/utils";

// Loading fallback rendered inside the React tree, so it can read translations.
const MapLoadingFallback = () => {
  const t = useTranslations("common.form");
  return (
    <div className="w-full h-[200px] bg-bg-secondary rounded-xs flex items-center justify-center">
      <p className="text-muted-foreground">{t("mapLoading")}</p>
    </div>
  );
};

// Dynamically import the map component to avoid SSR issues
const MapComponent = dynamic(() => import("./rhf-map-internal"), {
  ssr: false,
  loading: () => <MapLoadingFallback />,
});

interface CustomRHFMapProps<TFieldValues extends FieldValues>
  extends Pick<HTMLAttributes<HTMLDivElement>, "className"> {
  latName: FieldPath<TFieldValues>;
  lonName: FieldPath<TFieldValues>;
  addressName?: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: Pick<HTMLAttributes<HTMLDivElement>, "className">;
  defaultCenter?: [number, number];
  defaultZoom?: number;
}

export default function CustomRHFMap<TFieldValues extends FieldValues>({
  latName,
  lonName,
  addressName,
  helperText,
  containerClass,
  className,
  defaultCenter = [35.6892, 51.389], // Tehran, Iran default
  defaultZoom = 13,
}: CustomRHFMapProps<TFieldValues>) {
  const { control } = useFormContext<TFieldValues>();

  const {
    field: latField,
    fieldState: { error: latError },
  } = useController({
    name: latName,
    control,
  });

  const { field: lonField } = useController({
    name: lonName,
    control,
  });

  const lat = latField.value;
  const lon = lonField.value;

  // Use reverse geocoding hook if addressName is provided
  useReverseGeocode({
    control,
    latName,
    lonName,
    addressName,
    enabled: !!addressName,
  });

  const handleMapClick = (clickedLat: number, clickedLon: number) => {
    latField.onChange(clickedLat);
    lonField.onChange(clickedLon);
  };

  const handleLocationFound = (foundLat: number, foundLon: number) => {
    latField.onChange(foundLat);
    lonField.onChange(foundLon);
  };

  return (
    <div className={cn("space-y-2 w-full", containerClass?.className)}>
      <div
        className={cn(
          "w-full h-[200px] rounded-xs overflow-hidden border border-stroke-primary",
          className,
        )}
      >
        <MapComponent
          lat={lat}
          lon={lon}
          defaultCenter={defaultCenter}
          defaultZoom={defaultZoom}
          onMapClick={handleMapClick}
          onLocationFound={handleLocationFound}
        />
      </div>
      {!!helperText && (
        <p className="text-xs leading-tight text-muted-foreground ml-1.5">
          {helperText}
        </p>
      )}
      {!!latError && (
        <p className="text-xs leading-tight text-error ml-1.5">
          {latError?.message}
        </p>
      )}
    </div>
  );
}
